import React, {
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { AppBar, Box, Menu, MenuItem } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useAuth0 } from '@auth0/auth0-react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { OrgDropdown } from './OrgDropdown'
import { IOrganization } from './interfaces'
import { enqueueSnackbar } from 'notistack'
import {
    updateEnabledApplications,
    updateLoadingPermissions,
    updateOrg,
    updatePermissions,
    updateSharedOrgsList,
} from '../../../redux/slices/orgSlice'
import { useAppDispatch } from '../../../redux/hook'
import { getRoles } from '../../../redux/slices/rolesSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import UserProfileDetails from './UserProfileDialog'
import { useNavigate } from 'react-router-dom'
import { resetFacilityMonitoringState } from '../../../redux/slices/facilityMonitoringSlice'
import { resetInTransitState } from '../../../redux/slices/inTransitSlice'
import { createBrowserHistory } from 'history'
import { getUserData, getUserUnitsList } from '../../../redux/slices/userSlice'
import {
    fetchUserOrgPermissions,
    updateUserSessionsAudit,
} from '../../../services/userServices'
import { GetUserPermissions } from '../../../services/apiInterfaces'
import { RoutePaths, USER_SESSION_AUDIT } from '../../../constants/constants'
import APIToken from './APIToken'
import { useTranslation } from 'react-i18next'
import { updateSelectedEntity } from '../../../redux/slices/dtStudioSlice'
import { getEntities } from '../../../redux/slices/navSlice'
import theme from '../../../theme/theme'
import {
    setFilterList,
    resetEntityListColumns,
} from '../../../redux/slices/entitySlice'
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesProvider'

const Navbar = (): ReactElement => {
    const { t } = useTranslation()
    const muiTheme = useTheme()
    const { isAuthenticated, logout, loginWithRedirect, user } = useAuth0()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { guard } = useUnsavedChanges()
    // const route = useLocation()
    const history = createBrowserHistory()

    // const encodedUrl = route.pathname
    // const routePathname = encodedUrl.replace(/%20/g, ' ')

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const [openUserDetailsDialog, setOpenUserDetailsDialog] =
        useState<boolean>(false)

    const [openAPIToken, setOpenAPIToken] = useState<boolean>(false)
    const isGuestUser = globalThis.location.pathname.includes(
        RoutePaths.guestUserShipment,
    )
    const userEmail = user?.email || ''
    // useAppSelector((state) => state.searchParams.isGuestUser) === 'true'

    const handleLogout = useCallback(async () => {
        history.push(RoutePaths.Home)
        localStorage.removeItem('DEFAULT_ORG')
        await updateUserSessionsAudit(USER_SESSION_AUDIT.LOGOUT)
        logout({
            logoutParams: {
                returnTo: globalThis.location.origin,
            },
        })

        // this code for when user logout clearing the browser history. so, user can't came to prev page when click on browser back button

        const preventBack = () => {
            globalThis.history.forward()
        }
        setTimeout(() => {
            preventBack()
        }, 100)
        window.onunload = function () {}
    }, [history, logout])

    // const handleSignUp = useCallback(async () => {
    //     await loginWithRedirect({
    //         appState: {
    //             returnTo: RoutePaths.Home,
    //         },
    //         authorizationParams: {
    //             screen_hint: 'signup',
    //         },
    //     })
    // }, [loginWithRedirect])

    const handleLogin = useCallback(async () => {
        await loginWithRedirect({
            appState: {
                returnTo: RoutePaths.Home,
            },
        })
    }, [loginWithRedirect])

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const onClickUserProfileMenuLink = useCallback(() => {
        setOpenUserDetailsDialog(true)
        setAnchorEl(null)
    }, [])

    const handleAPIToken = useCallback(() => {
        setOpenAPIToken(true)
        setAnchorEl(null)
    }, [])

    const getAuthenticatedMenuItems = useCallback(() => {
        return [
            <MenuItem
                id="user-profile-menu-link"
                key="user"
                onClick={onClickUserProfileMenuLink}
            >
                {t('userProfile')}
            </MenuItem>,
            <MenuItem
                id="api-token-menu-link"
                key="api-token"
                onClick={handleAPIToken}
            >
                {t('apiToken')}
            </MenuItem>,
            <MenuItem
                id="logout-menu-link"
                key="logout"
                onClick={() => guard(handleLogout)}
            >
                {t('logout')}
            </MenuItem>,
        ]
    }, [t, onClickUserProfileMenuLink, handleAPIToken, handleLogout, guard])

    const getUnAuthenticatedMenuItems = useCallback(() => {
        return [
            <MenuItem
                id="sign-in-menu-link"
                key="sign-in"
                onClick={handleLogin}
            >
                {t('signIn')}
            </MenuItem>,
            // <MenuItem key="sign-up" onClick={handleSignUp}>
            //     Sign Up
            // </MenuItem>,
        ]
    }, [t, handleLogin])

    const onOrgChange = useCallback(() => {
        //Resetting Applications State
        dispatch(resetFacilityMonitoringState())
        dispatch(resetInTransitState())
        dispatch(setFilterList([]))
        dispatch(resetEntityListColumns())
        navigate(RoutePaths.Home)
    }, [dispatch, navigate])

    const displayOrgChangeNotification = useCallback(() => {
        enqueueSnackbar(t('organizationChangeSuccess'), {
            variant: 'success',
        })
    }, [t])

    const updateOrganization = useCallback(
        async (newOrgValue: IOrganization | null, isUpdatingOrg = false) => {
            dispatch(updateLoader(true))
            dispatch(updateLoadingPermissions(true))
            if (isUpdatingOrg) {
                onOrgChange()
            }
            if (newOrgValue?.id) {
                const response: GetUserPermissions =
                    await fetchUserOrgPermissions(newOrgValue.id)
                dispatch(updatePermissions(response.permissions))
                dispatch(
                    updateEnabledApplications(response.enabledApplications),
                )
                dispatch(updateSharedOrgsList(newOrgValue.id || ''))
            }
            if (isUpdatingOrg) {
                displayOrgChangeNotification()
            }
            localStorage.setItem('DEFAULT_ORG', JSON.stringify(newOrgValue))
            dispatch(updateSelectedEntity({})) // this for removing selected template for DT studio
            dispatch(getEntities(newOrgValue?.id || ''))
            dispatch(updateOrg(newOrgValue))
            dispatch(
                getUserData({ orgId: newOrgValue?.id || '', email: userEmail }),
            )
            dispatch(updateLoader(false))
            dispatch(updateLoadingPermissions(false))
        },
        [dispatch, onOrgChange, displayOrgChangeNotification, userEmail],
    )

    useEffect(() => {
        if (isAuthenticated && !isGuestUser) {
            dispatch(getRoles())
            dispatch(getUserUnitsList())
        }
    }, [dispatch, isAuthenticated, isGuestUser])

    return (
        <nav aria-label={t('primaryNavigation')}>
            <AppBar
                id="Top Navbar"
                position="fixed"
                sx={{
                    backgroundColor: theme.background.color,
                    zIndex: muiTheme.zIndex.drawer + 1,
                    boxShadow: 'none',
                    height: '60px',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            marginLeft: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            pt: '10px',
                            pl: '5px',
                        }}
                    >
                        <img
                            src="/assets/images/icons/UBQNetworkLogo.svg"
                            alt={t('ubqNetworkLogo')}
                            width="80px"
                            height="50px"
                            title={t('ubqNetworkLogo')}
                        />
                        <Box sx={{ width: '20px' }} />
                        <img
                            src="/assets/images/DT_Powered_By_Logo.png"
                            alt={t('deltaTrakPoweredByLogoWithBranding')}
                            width="180px"
                            title={t('deltaTrakPoweredByLogo')}
                        />
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            backgroundColor: theme.background.color,
                            zIndex: muiTheme.zIndex.drawer + 1,
                            backgroundImage:
                                'url(/assets/images/icons/AppBarBackgroundImage.svg)',
                            backgroundRepeat: 'no-repeat',
                            height: '83px',
                            maxWidth: '1000px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingRight: '25px',
                            }}
                        >
                            <Box
                                sx={{
                                    mr: 3,
                                    borderRadius: 3,
                                    width: '350px',
                                }}
                            >
                                {!isGuestUser && (
                                    <OrgDropdown
                                        updateOrg={updateOrganization}
                                    />
                                )}
                            </Box>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleClick}
                                    id="user-menu"
                                    role="button"
                                    aria-haspopup="menu"
                                    aria-label={t('openUserMenu')}
                                    tabIndex={0}
                                >
                                    <AccountCircleIcon titleAccess="Account" />
                                    <img
                                        src="/assets/images/icons/ArrowDropdown.svg"
                                        alt={t('dropdownArrowIcon')}
                                        width="10px"
                                        title={t('openUserMenu')}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {open ? (
                    <Menu
                        id="demo-positioned-menu"
                        aria-labelledby="user-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            role: 'menu',
                        }}
                    >
                        {isAuthenticated && !isGuestUser
                            ? getAuthenticatedMenuItems()
                            : getUnAuthenticatedMenuItems()}
                    </Menu>
                ) : null}
                {openUserDetailsDialog ? (
                    <UserProfileDetails
                        openDialog={openUserDetailsDialog}
                        handleClose={() => setOpenUserDetailsDialog(false)}
                    />
                ) : null}
                {openAPIToken ? (
                    <APIToken handleClose={() => setOpenAPIToken(false)} />
                ) : null}
            </AppBar>
        </nav>
    )
}

const MemoizedNavbar = memo(Navbar)
MemoizedNavbar.displayName = 'Navbar'

export default MemoizedNavbar
